import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import CustomHeader from "@src/components/common/appHeader/CustomHeader";
import Checkbox from "@src/components/common/checkbox/Checkbox";
import rightArrow from "@assets/images/icon/chevronright_g900.svg";
import WeddingHallContractCheckInfo from "./WeddingHallContractCheckInfo";
import SignInWeddingContract from "./SignInWeddingContract";
import { useDispatch, useSelector } from "react-redux";
import { setAgreement, weddingHallContract } from "@src/@slice/weddingHallContractSlice";
import { addComma } from "@src/utils";
import WeddingHallPolicies from "./WeddingHallPolicies";
import { MyWeddingContractInfo } from "@src/types/weddingHall";

/**
 * @param isOpen boolean - 모달창 여닫기
 * @param setClose open이라고 되어있지만.. 닫는 역할입니당...
 * @param onlyRead boolean - 마이웨딩 > 계약서에서 사용 시 true로 넘겨주시면 됩니다.
 * @param reservationInfo MyWeddingContractInfo - 마이웨딩 > 계약서에서 사용 시 최초 계약 정보입니다.
 */
type WeddingContractProps = {
  isOpen: boolean;
  setClose: () => void;
  onlyRead?: boolean;
  reservationInfo?: MyWeddingContractInfo;
};

const WeddingContract = ({
  isOpen = false,
  setClose,
  onlyRead = false,
  reservationInfo,
}: WeddingContractProps) => {
  const dispatch = useDispatch();
  const userData = useSelector(weddingHallContract).data.user; // 사용자 정보
  const agreeData = useSelector(weddingHallContract).data.agreement; // 사용자가 동의한 항목들
  const weddingContractData = useSelector(weddingHallContract).data.weddingContractData; // 예식계약서 정보들
  const { checkList, contractProduct, contractRefund, weddingContract, additionalInfo } = agreeData;
  const {
    weddingDate,
    weddingTime,
    mealPrice,
    totalPrice,
    regularPrice,
    providedServices,
    term,
    ...info
  } = weddingContractData;

  const [userTableData, setUserTableData] = useState<{ name: string; value: string | string[] }[]>(
    [],
  ); // 계약자 테이블 데이터
  const [weddingTableData, setWeddingTableData] = useState<
    { name: string; value: string | string[] }[]
  >([]); // 기본 계약사항 테이블 데이터
  const [foodTableData, setFoodTableData] = useState<{ name: string; value: string | string[] }[]>(
    [],
  ); // 식음료 테이블 데이터
  const [hallTableData, setTableData] = useState<{ name: string; value: string | string[] }[]>([]); // 대관상품 테이블 데이터
  const [providedServiceData, setProvidedServiceData] = useState<
    { name: string; value: string | string[] }[]
  >([]); // 예식서비스 데이터
  const [productAgree, setProductAgree] = useState(agreeData.contractProduct); // 대관료 동의
  const [refundAgree, setRefundAgree] = useState(agreeData.contractRefund); // 약관 및 취소 동의
  const [showSignSheet, setShowSignSheet] = useState(false); // 서명하기 바텀시트

  const [openModalInfo, setOpenModalInfo] = useState({
    open: false,
    title: "",
    target: "",
    src: null,
  }); //확인사항 이미지 모달

  const [openPolicyModal, setOpenPolicyModal] = useState({
    target: "",
    open: false,
    onlyRead: onlyRead,
    policy: "",
  });

  const closeModal = () => setOpenModalInfo({ ...openModalInfo, open: false });
  const closePolicyModal = () => setOpenPolicyModal({ ...openPolicyModal, open: false });


//   const closePolicyModal = () => setOpenPolicyModal({ ...openPolicyModal, open: false });

  useEffect(() => {
    if (onlyRead) {
      //마이웨딩 예식 계약서 정보 조회
      const contractor = reservationInfo.firstPaper.contentJson.contract.contractor;
      const customer = reservationInfo.firstPaper.contentJson.contract.customer;
      const weddingDay = reservationInfo.firstPaper.contentJson.weddingDay;
      const weddingConfiguration = reservationInfo.firstPaper.contentJson.weddingConfiguration;
      const partner = reservationInfo.profileJson;

      setUserTableData([
        { name: "계약자 이름", value: contractor.name },
        { name: "핸드폰 번호", value: contractor.tel },
        { name: "이메일", value: contractor.email },
        { name: "주소", value: contractor.address1 + " " + contractor.address2 },
      ]);
      setWeddingTableData([
        {
          name: "예식일자",
          value: `${dayjs(weddingDay.weddingDate).format("YYYY년 MM월 DD일 ddd요일")} ${weddingDay.weddingTime.slice(0, 5)}`,
        },
        { name: "예식장소", value: `${partner.name} ${weddingDay.individualHallName}` },
        { name: "지불보증인원", value: `최소 ${weddingDay.guarantee}명(대인기준)` },
        { name: "신부", value: [customer.brideName, customer.brideTel] },
        { name: "신랑", value: [customer.groomName, customer.groomTel] },
      ]);
      setFoodTableData([
        {
          name: "식대(대인)",
          value: `${addComma(weddingDay.foodAndBeverage.adultFee)}원 (1인 식대)`,
        },
        {
          name: "식대(소인)",
          value: `${addComma(weddingDay.foodAndBeverage.childFee)}원 (1인 식대)`,
        },
        { name: "음료", value: weddingDay.foodAndBeverage.beverageFee > 0 ? `${addComma(weddingDay.foodAndBeverage.beverageFee)}원`: '포함'},
        { name: "주류", value: weddingDay.foodAndBeverage.alcoholFee > 0 ? `${addComma(weddingDay.foodAndBeverage.alcoholFee)}원` : '포함'},
        { name: "답례품", value: `${addComma(weddingDay.foodAndBeverage.giftFee)}원` },
      ]);
      setTableData([
        { name: "대관료", value: `${addComma(weddingDay.rentalFee)}원` },
        { name: "플라워", value: `${addComma(weddingDay.flowerFee)}원` },
        ...weddingDay.additionalProducts.map((product) => ({
          name: product.name,
          value: `${addComma(product.price)}원`,
        })),
      ]);
      if (
        weddingConfiguration.providedServices &&
        weddingConfiguration.providedServices.length > 0
      ) {
        setProvidedServiceData([
          {
            name: "예식서비스",
            value: weddingConfiguration.providedServices.map(({ name }) => name).join(`\n`),
          },
        ]);
      }
    } else {
        // 웨딩홀 실시간 계약 > 결제 확인 > 예식 계약서 데이터
      const services = providedServices.map((service) => service.name);
      setUserTableData([
        { name: "계약자 이름", value: userData.contractor },
        { name: "핸드폰 번호", value: userData.contractorTel },
        { name: "이메일", value: userData.contractorEmail },
        { name: "주소", value: userData.contractorAddress1 + " " + userData.contractorAddress2 },
      ]);
      setWeddingTableData([
        {
          name: "예식일자",
          value: `${dayjs(weddingDate.replaceAll('.','-')).format("YYYY년 MM월 DD일 ddd요일")} ${weddingTime}`,
        },
        { name: "예식장소", value: `${info.partnerName} ${info.individualHallName}` },
        { name: "지불보증인원", value: `최소 ${info.guarantee}명(대인기준)` },
        { name: "신부", value: [userData.brideName, userData.brideTel] },
        { name: "신랑", value: [userData.groomName, userData.groomTel] },
      ]);
      setFoodTableData([
        { name: "식대(대인)", value: `${addComma(mealPrice.adultFee)}원 (1인 식대)` },
        { name: "식대(소인)", value: `${addComma(mealPrice.childFee)}원 (1인 식대)` },
        { name: "음료", value: mealPrice.beverageFee > 0 ? `${addComma(mealPrice.beverageFee)}원`: '포함'},
        { name: "주류", value: mealPrice.alcoholFee > 0 ? `${addComma(mealPrice.alcoholFee)}원` : '포함'},
        { name: "답례품", value: `${addComma(mealPrice.giftFee)}원` },
      ]);
      setTableData([
        { name: "대관료", value: `${addComma(regularPrice.rentalFee)}원` },
        { name: "플라워", value: `${addComma(regularPrice.flowerFee)}원` },
        ...regularPrice.additionalProducts.map((product) => ({
          name: product.name,
          value: `${addComma(product.price)}원`,
        })),
      ]);
      if (
        services &&
        services.length > 0
      ){      
        setProvidedServiceData([{ name: "예식서비스", value: services.join(`\n`) }]);
      }

    }
  }, []);

  useEffect(() => {
    setProductAgree(agreeData.contractProduct);
    setRefundAgree(agreeData.contractRefund);
  }, [agreeData.contractProduct,agreeData.contractRefund]);

  const completeSign = () => {
    setShowSignSheet(false);
    dispatch(setAgreement({keyname: "finalAgree",checked: true,}));
    setClose();
  };

  return (
    <>
      {isOpen && (
        <div className="page-wedding-contract modal-container hide-scroll">
          <div className="modal-inner">
          {!openModalInfo.open && !openPolicyModal.open && 
          <CustomHeader goBack={() => setClose()} style={{WebkitTransform: 'translate3d(0, 0, 0)', transform: 'translate3d(0, 0, 0)'}}/>}
            <div className="modal-contents">
              <p className="bold">예식 계약서</p>
              <div className="table-wrap">
                <TableSection type="table" title="계약자" datas={userTableData} />
                <TableSection type="table" title="기본 계약사항" datas={weddingTableData} />
                <TableSection type="table" title="식대 및 음료" datas={foodTableData} />
                <TableSection type="table" title="대관 상품" datas={hallTableData} />
                {providedServiceData.length !== 0 && (
                  <TableSection type="table" title="예식서비스" datas={providedServiceData} />
                )}
                <PolicyViewSection
                  onlyRead={onlyRead}
                  additionalInformation={
                    onlyRead
                      ? reservationInfo.firstPaper.contentJson.weddingConfiguration
                          .additionalInformation
                      : info.additionalInformation
                  }
                  files={
                    onlyRead
                      ? reservationInfo.firstPaper.contentJson.weddingConfiguration.files
                      : info.files
                  }
                  policys={
                    onlyRead
                      ? {
                          term: reservationInfo.firstPaper.contentJson.weddingConfiguration.term,
                          agreement:
                            reservationInfo.firstPaper.contentJson.weddingConfiguration.agreement,
                        }
                      : { term: "", agreement: "" }
                  }
                  setOpenModalInfo={setOpenModalInfo}
                  setOpenPolicyModal={setOpenPolicyModal}
                />
                {openModalInfo.open && 
                <WeddingHallContractCheckInfo openModalInfo={openModalInfo} closeModal={closeModal} />}
                {openPolicyModal.open && (
                    <WeddingHallPolicies openModalInfo={openPolicyModal} closeModal={closePolicyModal} />
                )}
              </div>

              {/** 실시간 계약인 경우 약관 동의 및 확인 버튼 UI */}
              {onlyRead === false && (
                <>
                  <div className="divider" />
                  <div className="agree-wrap">
                    <div className="agree-check-wrap">
                      <Checkbox
                        id={`product-agree-policy`}
                        name="policy"
                        value=""
                        setValue={() => {
                          setProductAgree(!productAgree);
                          dispatch(setAgreement({ keyname: "contractProduct", checked: !productAgree }),);
                        }}
                        checked={productAgree}
                      />
                      <p>
                        (필수) 대관료 및 추가상품 확인했습니다.동의를 모두 읽었으며 이에 동의합니다.
                      </p>
                    </div>
                    <div className="agree-check-wrap">
                      <Checkbox
                        id={`refund-agree-policy`}
                        name="policy"
                        value=""
                        setValue={() => {
                          if(refundAgree === false){
                            setOpenPolicyModal({target: "2",open: true,onlyRead: false,policy: term})
                          }else{
                            dispatch(setAgreement({ keyname: "contractRefund", checked: !refundAgree }));
                          }
                        }}
                        checked={refundAgree}
                        onClick={() =>{
                          if(refundAgree === false){
                            setOpenPolicyModal({target: "2",open: true,onlyRead: false,policy: term})
                          }
                          dispatch(setAgreement({ keyname: "contractRefund", checked: !refundAgree }));
                        }
                      }
                      />
                      <p>
                        (필수){" "}
                        <span
                          className="bold"
                          onClick={() =>
                            setOpenPolicyModal({
                              target: "2",
                              open: true,
                              onlyRead: false,
                              policy: term,
                            })
                          }
                        >
                          약관 내용 및 취소 수수료와 위약금
                        </span>
                        <span>에 대해 모두 읽었으며 이에 동의합니다.</span>
                      </p>
                    </div>
                  </div>
                  <div className="btn-wrap">
                    <button
                      className="btn btn-large btn-gray50"
                      onClick={() => {
                        if (productAgree && refundAgree) {
                          setClose();
                        } else {
                          setProductAgree(false);
                          setRefundAgree(false);
                          dispatch(setAgreement({ keyname: "contractProduct", checked: false }));
                          dispatch(setAgreement({ keyname: "contractRefund", checked: false }));
                          setClose();
                        }
                      }}
                    >
                      닫기
                    </button>
                    <button
                      className={`btn btn-giant btn-full ${!(checkList && contractProduct && contractRefund && additionalInfo) || weddingContract === true ? "btn-disabled": "btn-red500"}`}
                      disabled={!(checkList && contractProduct && contractRefund && additionalInfo) || weddingContract === true}
                      onClick={() => {
                        setShowSignSheet(true)
                      }}
                    >
                      서명하기
                    </button>
                  </div>
                  <SignInWeddingContract
                    isShow={showSignSheet}
                    completeSign={completeSign}
                    closeModal={() => setShowSignSheet(false)}
                  />
                </>
              )}
              {/**여기까지 */}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeddingContract;



// *******************************************


type Props = {
  type: "table" | "input" | "list";
  title: string;
  datas: { name: string; value: string | string[] }[];
};

const TableSection = ({ title = "", datas }: Props) => {
  return (
    <div>
      {title.length > 0 && <p className="title bold">{title}</p>}
      <table>
        <tbody>
          {datas.map((data, idx) => (
            <tr key={idx}>
              <td className="label bold">{data.name}</td>
              <td style={{ whiteSpace: "pre-line" }}>
                {typeof data.value === "string" ? data.value : data.value.join(`\n`)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


// 확인사항 테이블
const PolicyViewSection = ({
  onlyRead = false,
  additionalInformation,
  files,
  policys,
  setOpenModalInfo,
  setOpenPolicyModal
}) => {
  const dispatch = useDispatch();
  const alreadyChecked = useSelector(weddingHallContract).data.agreement.checkList;
  const additionalInfoChecked = useSelector(weddingHallContract).data.agreement.additionalInfo;

  const [isCheckAddtionalInfo, setIsChckAddionalIno] = useState(additionalInfoChecked); // 추가사항
  const [isCheckInfos, setIsCheckInfos] = useState([]); //확인사항 파일목록

  //확인사항 체크하는 배열 초기화
  useEffect(() => {
    if(files.length > 0){
        if (alreadyChecked) {
            const checkArray = Array.from({ length: files.length }, () => true);
            setIsCheckInfos(checkArray);
            } else {
            const checkArray = Array.from({ length: files.length }, () => false);
            setIsCheckInfos(checkArray);
        }
    }
  }, []);


  //추가정보와 파일목록이 없을 때 true로 간주
  useEffect(() => {
    if(files.length === 0){
        dispatch(setAgreement({keyname: "checkList",checked: true,}),);
    }
    if(additionalInformation === null || additionalInformation === ''){
        dispatch(setAgreement({keyname: "additionalInfo",checked: true,}),);
    }
  }, []);

  return (
    <>
      <div className="policy-view">
        {/* additionalInformation이 null or ''인 경우 안보여주기 */}
        {(additionalInformation !== null && additionalInformation !== '') && (
          <div>
            <p className="title bold">추가사항</p>
            <div className="policy-view-item-single">
              <p className="label bold">
                {onlyRead || (
                  <Checkbox
                    id={`check-info-img`}
                    name={`check-info-img`}
                    value=""
                    setValue={() => {
                      setIsChckAddionalIno(!isCheckAddtionalInfo);
                      dispatch(
                        setAgreement({
                          keyname: "additionalInfo",
                          checked: !isCheckAddtionalInfo,
                        }),
                      );
                    }}
                    checked={isCheckAddtionalInfo}
                  />
                )}
                <span style={{ marginLeft: onlyRead ? "0px" : "8px" }}>자세히보기</span>
              </p>
              <img
                src={rightArrow}
                alt=""
                onClick={() =>
                  setOpenModalInfo({
                    open: true,
                    title: "추가사항",
                    target: "string",
                    src: additionalInformation,
                  })
                }
              />
            </div>
          </div>
        )}
        <div>
          {onlyRead === false ? (
            // 실시간 계약 > 결제 확인 UI
            <>
            {!!files.length && 
            <>
            <p className="title bold">확인사항</p>
            <div className="policy-view-item-multi ">
              {files.map((file, idx) => (
                  <div>
                    <p className="label bold">
                      <Checkbox
                        id={`check-info-img${idx}`}
                        name={`check-info-img${idx}`}
                        value=""
                        setValue={() => {
                          const newCheckArray = [...isCheckInfos];
                          newCheckArray[idx] = !isCheckInfos[idx];
                          setIsCheckInfos(newCheckArray);
                          dispatch(
                            setAgreement({
                              keyname: "checkList",
                              checked: newCheckArray.every((value) => value === true),
                            }),
                          );
                        }}
                        checked={isCheckInfos[idx]}
                      />
                      <span style={{ marginLeft: "8px" }}>{file.name}</span>
                    </p>
                    <img
                      src={rightArrow}
                      alt=""
                      onClick={() =>
                        setOpenModalInfo({
                          open: true,
                          title: file.name,
                          target: "img",
                          src: file.url || "",
                        })
                      }
                    />
                  </div>
                
                ))}
            </div>
            </>
            }
          
            </>
          ) : (
            // 마이웨딩 > 계약서 UI
            <>
            <p className="title bold">확인사항</p>
            <div className="policy-view-item-multi">
              {files &&
                files.map((file) => (
                  <div>
                    <p className="label bold">
                      <span>{file.name}</span>
                    </p>
                    <img
                      src={rightArrow}
                      alt=""
                      onClick={() =>
                        setOpenModalInfo({
                          open: true,
                          title: file.name,
                          target: "img",
                          src: file.url,
                        })
                      }
                    />
                  </div>
                ))}
              <div>
                <p className="label bold">
                  <span>이용약관</span>
                </p>
                <img
                  src={rightArrow}
                  alt=""
                  onClick={() =>
                    setOpenPolicyModal({
                      target: "2",
                      open: true,
                      onlyRead: true,
                      policy: policys.term,
                    })
                  }
                />
              </div>
              <div>
                <p className="label bold">
                  <span>개인정보수집 제공 및 활용 동의</span>
                </p>
                <img
                  src={rightArrow}
                  alt=""
                  onClick={() =>
                    setOpenPolicyModal({
                      target: "0",
                      open: true,
                      onlyRead: true,
                      policy: policys.agreement,
                    })
                  }
                />
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
